import { Link, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { toast } from 'material-react-toastify';
import { Box, IconButton, Paper, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid2';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Order } from '../../../../interfaces/Order/Order';
import { getOrderByID } from '../../../../api/Order';
import { Waiting } from '../../../../components/Waiting/Waiting';
import { bodyWidth, sharedColors } from '../../../../utils/Style';
import { formatLocalTime } from '../../../../utils/Date';
import { OrderBreakdownTable } from './OrderBreakdownTable';
import { AddressCard } from '../../../../components/Address/AddressCard';
import { addressesFromOrder } from '../../../../interfaces/Address/Address';
import { GenericHelmet } from '../../../../components/Helmet/GenericHelmet';
import { OfferSummary } from '../../../../interfaces/Offer/OfferSummary';
import { getOffer } from '../../../../api/Offer';
import { OfferCard } from '../../../../components/Offer/OfferCard';
import { useLocalizedText } from '../../../../hooks/useLocalizedText';

type OrderDetailsKeyValueProps = {
  label: string;
  value: string;
  link?: string;
};

const OrderDetailsKeyValue = ({
  label,
  value,
  link,
}: OrderDetailsKeyValueProps) => {
  const renderedValue = (
    <Typography
      sx={{
        fontSize: '14px',
        fontWeight: 600,
        color: sharedColors.gray7,
        lineBreak: 'anywhere',
      }}
    >
      {value}
    </Typography>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
      <Typography
        noWrap
        sx={{ fontSize: '14px', color: sharedColors.gray5, flex: '0 0 auto' }}
      >
        {label}:
      </Typography>

      {link ? <Link to={link}>{renderedValue}</Link> : renderedValue}
    </Box>
  );
};

export const OrderDetailsPage = () => {
  const t = useLocalizedText();

  const navigate = useNavigate();

  const { orderID: orderIDAsStr } = useParams<string>();

  const orderID = orderIDAsStr ? Number(orderIDAsStr) : -1;

  const [order, setOrder] = useState<Order | undefined>(undefined);
  const [offer, setOffer] = useState<OfferSummary | undefined>(undefined);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [loadingOffer, setLoadingOffer] = useState(false);

  const hasOffer = Number.isFinite(order?.offerID);

  const refreshOrder = () => {
    setLoadingOrder(true);
    getOrderByID(orderID)
      .then((o) => setOrder(o))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingOrder(false));
  };

  const refreshOffer = () => {
    if (!hasOffer) {
      setOffer(undefined);
      return;
    }

    setLoadingOffer(true);
    getOffer(order!.offerID!)
      .then((o) => setOffer(o))
      .catch((err) => toast.error(err.message))
      .finally(() => setLoadingOffer(false));
  };

  useEffect(() => {
    refreshOrder();
  }, [orderID]);

  useEffect(() => {
    refreshOffer();
  }, [order?.offerID]);

  if (loadingOrder) {
    return <Waiting open />;
  }

  if (!order) {
    return <Typography>{t('order.details_page.not_found')}</Typography>;
  }

  const { shipping, billing } = addressesFromOrder(order);

  const addressGridSize = {
    xs: 12,
    lg: 6,
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
      }}
    >
      <GenericHelmet title={t('order.details_page.order_details')} />

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: bodyWidth,
          py: 3,
          px: 1.5,
          gap: 2.5,
          boxSizing: 'border-box',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <IconButton onClick={() => navigate('/hesabim/siparislerim')}>
            <ArrowBackIcon />
          </IconButton>

          <Typography sx={{ fontSize: '1.75rem', fontWeight: 600 }}>
            {t('order.details_page.order_details')}
          </Typography>
        </Box>

        <Paper
          elevation={3}
          sx={{
            px: 1.5,
            py: 1.5,
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            gap: 1.5,
            width: '100%',
          }}
        >
          <OrderDetailsKeyValue
            label={t('order.details_page.id')}
            value={order.id.toString()}
          />
          <OrderDetailsKeyValue
            label={t('order.details_page.order_date')}
            value={formatLocalTime(order.createdAt)}
          />
          <OrderDetailsKeyValue
            label={t('order.details_page.payment_method')}
            value={order.paymentMethod}
          />
          <OrderDetailsKeyValue
            label={t('order.details_page.status')}
            value={order.status}
          />
          {!!order.cargoURL && (
            <OrderDetailsKeyValue
              label={t('order.details_page.cargo_url')}
              value={order.cargoURL}
              link={order.cargoURL}
            />
          )}
        </Paper>

        {hasOffer && !offer && loadingOffer && <OfferCard applied />}
        {!!offer && <OfferCard offer={offer} applied />}

        <OrderBreakdownTable order={order} />

        {!!order.notes && (
          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              gap: 1.5,
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {t('order.details_page.order_notes')}
            </Typography>
            <TextField
              size='small'
              multiline
              minRows={2}
              disabled
              value={order.notes}
            />
          </Paper>
        )}

        <Grid container spacing={2} width='100%'>
          <Grid size={addressGridSize}>
            <AddressCard address={billing} isBilling hideEditButton />
          </Grid>
          <Grid size={addressGridSize}>
            <AddressCard address={shipping} isShipping hideEditButton />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};
